import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './stores/authStore';
import { useSiteConfigStore } from './stores/siteConfigStore';
import appConfig from './config/appConfig';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
// SettingsPage consolidated into SystemManagementPage

// Notifications toast popup
import NotificationToast from './components/NotificationToast';

// Layout
import Layout from './components/Layout';

// UI kit — provider hộp thoại xác nhận + nhập liệu dùng chung (thay window.confirm/prompt)
import { ConfirmProvider, PromptProvider } from './components/ui';

// Forced first-login password change gate
import ForcePasswordChange from './components/ForcePasswordChange';


// ---------------------------------------------------------------------------------------------------------------------------
// Tải trang theo TUYẾN (code-splitting). Bản cũ nhập tĩnh cả 37 trang nên tệp main.js ~5,9 MB (1,3 MB nén) phải tải xong mới hiện
// được màn đăng nhập; nay main còn ~0,66 MB (0,2 MB nén), mỗi trang tải khi vào. Sau mỗi lần deploy, tab đang mở có thể xin một tệp trang
// đã đổi tên (băm nội dung) → máy chủ trả index.html → "Loading chunk failed"; gặp lỗi đó thì tải lại trang MỘT lần (cờ trong
// sessionStorage chống vòng lặp, xoá cờ khi tải được) để lấy bản mới.
function lazyPage(loader) {
  return React.lazy(() => loader().then((mod) => {
    try { sessionStorage.removeItem('chunk-reloaded'); } catch (e) { /* bỏ qua */ }
    return mod;
  }).catch((err) => {
    let reloaded = false;
    try { reloaded = sessionStorage.getItem('chunk-reloaded') === '1'; } catch (e) { /* bỏ qua */ }
    if (!reloaded) {
      try { sessionStorage.setItem('chunk-reloaded', '1'); } catch (e) { /* bỏ qua */ }
      window.location.reload();
      return new Promise(() => {});   // chờ trang tải lại
    }
    throw err;
  }));
}
function PageFallback() {
  return (
    <div className="py-24 flex justify-center" role="status" aria-label="Đang tải trang">
      <div className="w-8 h-8 rounded-full border-2 border-line border-t-brand animate-spin" />
    </div>
  );
}

const AttendancePage = lazyPage(() => import('./pages/AttendancePage'));
const ShiftsPage = lazyPage(() => import('./pages/ShiftsPage'));
const SchedulingPage = lazyPage(() => import('./pages/SchedulingPage'));
const ReportsPage = lazyPage(() => import('./pages/ReportsPage'));
const ProfilePage = lazyPage(() => import('./pages/ProfilePage'));
const FaceRegistrationPage = lazyPage(() => import('./pages/FaceRegistrationPage'));
const GuidedFaceEnrollmentPage = lazyPage(() => import('./pages/GuidedFaceEnrollmentPage'));
const EmployeePortalPage = lazyPage(() => import('./pages/EmployeePortalPage'));
const EmployeeManagementPage = lazyPage(() => import('./pages/EmployeeManagementPage'));
const HRManagementPage = lazyPage(() => import('./pages/HRManagementPage'));
const SystemManagementPage = lazyPage(() => import('./pages/SystemManagementPage'));
const AIAutoCheckPage = lazyPage(() => import('./pages/AIAutoCheckPage'));
const LeaveRequestPage = lazyPage(() => import('./pages/LeaveRequestPage'));
const ApprovalPage = lazyPage(() => import('./pages/ApprovalPage'));
const AttendanceManagementPage = lazyPage(() => import('./pages/AttendanceManagementPage'));
const DepartmentDelegationPage = lazyPage(() => import('./pages/DepartmentDelegationPage'));
const OvertimeRequestPage = lazyPage(() => import('./pages/OvertimeRequestPage'));
const SupervisionPage = lazyPage(() => import('./pages/SupervisionPage'));
const PayrollApprovalPage = lazyPage(() => import('./pages/PayrollApprovalPage'));
const PayrollViewPage = lazyPage(() => import('./pages/PayrollViewPage'));
const OperationsMonitoringPage = lazyPage(() => import('./pages/OperationsMonitoringPage'));
const NotificationsPage = lazyPage(() => import('./pages/NotificationsPage'));
const OffSiteAttendancePage = lazyPage(() => import('./pages/OffSiteAttendancePage'));
const CollectiveAttendancePage = lazyPage(() => import('./pages/CollectiveAttendancePage'));
const DiscoveryPolicyPage = lazyPage(() => import('./pages/DiscoveryPolicyPage'));
const DiscoveryPilotDashboardPage = lazyPage(() => import('./pages/DiscoveryPilotDashboardPage'));
const DiscoveryTemplatesPage = lazyPage(() => import('./pages/DiscoveryTemplatesPage'));
const DiscoveryAnalyticsPage = lazyPage(() => import('./pages/DiscoveryAnalyticsPage'));
const AssetManagementPage = lazyPage(() => import('./pages/AssetManagementPage'));
const MealReportingPage = lazyPage(() => import('./pages/MealReportingPage'));

// Initialize app configuration
appConfig.initialize();

// Create query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Public Route Component - redirect to home if already authenticated
function PublicRoute({ children }) {
  const { isAuthenticated, accessToken, validateSession, user } = useAuthStore();
  const [isChecking, setIsChecking] = React.useState(isAuthenticated && !!accessToken);

  React.useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      setIsChecking(false);
      return;
    }

    let mounted = true;
    const check = async () => {
      const valid = await validateSession();
      if (mounted) setIsChecking(false);
      // if valid, the component will re-render with isAuthenticated=true and redirect
      if (!valid) {
        // token invalid — authStore already cleared, show login
      }
    };
    check();

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Đang xác thực...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    const roleCode = useAuthStore.getState().user?.role?.role_code;
    const isManager = ['ADMIN', 'DIRECTOR', 'MANAGER'].includes(roleCode);
    return <Navigate to={isManager ? '/' : '/portal'} replace />;
  }

  return children;
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, isValidating, validateSession, loadPagePermissions } = useAuthStore();
  const [isChecking, setIsChecking] = React.useState(true);

  // Validate session với server khi mount
  React.useEffect(() => {
    let mounted = true;
    
    // Abort validation if on chrome-error page
    try {
      if (window.location.href.includes('chrome-error://') || window.location.protocol === 'chrome-error:') {
        console.warn('[ProtectedRoute] On chrome-error page — skipping validation');
        return;
      }
    } catch (e) { /* ignore */ }

    const checkAuth = async () => {
      if (isAuthenticated) {
        console.log('[ProtectedRoute] Validating session...');
        await validateSession();
        // Load page permissions after session validation
        await loadPagePermissions();
      }
      if (mounted) {
        setIsChecking(false);
      }
    };
    
    checkAuth();
    
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Chỉ check khi mount

  // Show loading while checking
  if (isChecking || isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Đang xác thực...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Forced password change gate — chặn toàn bộ ứng dụng cho đến khi
// nhân viên đổi mật khẩu (sau khi mật khẩu bị admin/hệ thống reset).
function ForcePasswordChangeGate({ children }) {
  const user = useAuthStore((s) => s.user);
  if (user?.must_change_password) {
    return <ForcePasswordChange />;
  }
  return children;
}

// Page Access Protected Route - check if user has access to specific page
function PageProtectedRoute({ children, pagePath }) {
  const { hasPageAccess, pagePermissions, user } = useAuthStore();
  
  // If pagePermissions not loaded yet, show loading
  if (pagePermissions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Check access
  if (!hasPageAccess(pagePath)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <div className="bg-red-100 dark:bg-red-900/20 p-4 rounded-full mb-4">
          <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Không có quyền truy cập
        </h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-md">
          Bạn không có quyền truy cập vào trang này. Vui lòng liên hệ quản trị viên nếu bạn cần quyền truy cập.
        </p>
      </div>
    );
  }

  return children;
}

// Smart index redirect: managers+ → dashboard, others → portal
function IndexRedirect() {
  const { user } = useAuthStore();
  const roleCode = user?.role?.role_code;
  const isManager = ['ADMIN', 'DIRECTOR', 'MANAGER'].includes(roleCode);
  return isManager ? (
    <PageProtectedRoute pagePath="/"><DashboardPage /></PageProtectedRoute>
  ) : (
    <Navigate to="/portal" replace />
  );
}

function App() {
  const { fetchConfig } = useSiteConfigStore();

  React.useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ConfirmProvider>
        <PromptProvider>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />

            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ForcePasswordChangeGate>
                    <Layout />
                  </ForcePasswordChangeGate>
                </ProtectedRoute>
              }
            >
              <Route index element={<IndexRedirect />} />
              <Route path="portal" element={<PageProtectedRoute pagePath="/portal"><EmployeePortalPage /></PageProtectedRoute>} />
              <Route path="attendance" element={<PageProtectedRoute pagePath="/attendance"><AttendancePage /></PageProtectedRoute>} />
              <Route path="attendance-management" element={<PageProtectedRoute pagePath="/attendance-management"><AttendanceManagementPage /></PageProtectedRoute>} />
              {/* Tự gate bằng API context (trưởng phòng dù role=EMPLOYEE vẫn vào được) — KHÔNG dùng PageProtectedRoute */}
              <Route path="department-delegation" element={<DepartmentDelegationPage />} />
              <Route path="employee-management" element={<PageProtectedRoute pagePath="/employee-management"><EmployeeManagementPage /></PageProtectedRoute>} />
              <Route path="face-registration" element={<PageProtectedRoute pagePath="/face-registration"><FaceRegistrationPage /></PageProtectedRoute>} />
              <Route path="face-enrollment" element={<PageProtectedRoute pagePath="/face-registration"><GuidedFaceEnrollmentPage /></PageProtectedRoute>} />
              <Route path="shifts" element={<PageProtectedRoute pagePath="/shifts"><ShiftsPage /></PageProtectedRoute>} />
              <Route path="scheduling" element={<PageProtectedRoute pagePath="/scheduling"><SchedulingPage /></PageProtectedRoute>} />
              <Route path="leave-requests" element={<PageProtectedRoute pagePath="/leave-requests"><LeaveRequestPage /></PageProtectedRoute>} />
              <Route path="approval-process" element={<PageProtectedRoute pagePath="/approval-process"><ApprovalPage /></PageProtectedRoute>} />
              <Route path="overtime-requests" element={<PageProtectedRoute pagePath="/overtime-requests"><OvertimeRequestPage /></PageProtectedRoute>} />
              <Route path="off-site-attendance" element={<PageProtectedRoute pagePath="/off-site-attendance"><OffSiteAttendancePage /></PageProtectedRoute>} />
              <Route path="collective-attendance" element={<PageProtectedRoute pagePath="/collective-attendance"><CollectiveAttendancePage /></PageProtectedRoute>} />
              <Route path="reports" element={<PageProtectedRoute pagePath="/reports"><ReportsPage /></PageProtectedRoute>} />
              <Route path="settings" element={<Navigate to="/system-management" replace />} />
              <Route path="hr-management" element={<PageProtectedRoute pagePath="/hr-management"><HRManagementPage /></PageProtectedRoute>} />
              <Route path="supervision" element={<PageProtectedRoute pagePath="/supervision"><SupervisionPage /></PageProtectedRoute>} />
              <Route path="payroll-approval" element={<PageProtectedRoute pagePath="/payroll-approval"><PayrollApprovalPage /></PageProtectedRoute>} />
              <Route path="payroll-view" element={<PageProtectedRoute pagePath="/payroll-view"><PayrollViewPage /></PageProtectedRoute>} />
              <Route path="system-management" element={<PageProtectedRoute pagePath="/system-management"><SystemManagementPage /></PageProtectedRoute>} />
              <Route path="ai-auto-check" element={<PageProtectedRoute pagePath="/ai-auto-check"><AIAutoCheckPage /></PageProtectedRoute>} />
              <Route path="ai-analytics" element={<Navigate to="/ai-auto-check?area=quality&view=trend" replace />} />
              <Route path="operations-monitoring" element={<PageProtectedRoute pagePath="/operations-monitoring"><OperationsMonitoringPage /></PageProtectedRoute>} />
              <Route path="asset-management" element={<PageProtectedRoute pagePath="/asset-management"><AssetManagementPage /></PageProtectedRoute>} />
              <Route path="meal-reporting" element={<PageProtectedRoute pagePath="/meal-reporting"><MealReportingPage /></PageProtectedRoute>} />
              {/* Discovery ops layer — gated by nav role + backend authorize() (no page-permission seeding needed) */}
              <Route path="discovery-policy" element={<DiscoveryPolicyPage />} />
              <Route path="discovery-pilot" element={<DiscoveryPilotDashboardPage />} />
              <Route path="discovery-templates" element={<DiscoveryTemplatesPage />} />
              <Route path="discovery-analytics" element={<DiscoveryAnalyticsPage />} />
              <Route path="profile" element={<PageProtectedRoute pagePath="/profile"><ProfilePage /></PageProtectedRoute>} />
              <Route path="notifications" element={<NotificationsPage />} />
              {/* Redirects for removed standalone pages */}
              <Route path="department-management" element={<Navigate to="/system-management" replace />} />
              <Route path="settings-management" element={<Navigate to="/system-management" replace />} />
              <Route path="permission-management" element={<Navigate to="/system-management" replace />} />
              <Route path="position-management" element={<Navigate to="/system-management" replace />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>

          {/* Notification toast popups (bottom-right, like Zalo) */}
          <NotificationToast />

          {/* System Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10b981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 4000,
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
        </PromptProvider>
        </ConfirmProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
